<template>
	<section id="invoices" class="org-settings__plan-and-billing-table scroll-mt-4 b9:w-1/2 b12:w-1/3">
		<div class="flex items-center justify-between mb-4">
			<h2 class="font-display">{{ tableTitle }}</h2>
		</div>
		<CCallout v-if="showPastDueAlert" type="error" size="s" class="!text-primary mb-4">
			{{ pastDueAlertMessage }}
			<button type="button" class="button button--no-style text-link underline ml-1" @click="$emit('pay-now')">Pay Now</button>.
		</CCallout>
		<div ref="container" class="org-settings__plan-and-billing-table-container relative">
			<table class="table-style w-full">
				<thead v-if="useNewBillingPipeline">
					<tr>
						<th class="text-left">Billing Date</th>
						<th class="text-left">Status</th>
						<th class="text-right">Price</th>
					</tr>
				</thead>
				<tbody>
					<tr v-if="!hasInvoices">
						<td :colspan="3" class="text-subtle">
							No invoices found for the selected period.
						</td>
					</tr>
					<tr
						v-for="invoice in sortedInvoices"
						v-else
						:key="invoice.Id"
						:class="{ 'hidden-invoice-row': invoice.IsHidden }"
						@mouseenter="onRowEnter(invoice, $event)"
						@mouseleave="onRowLeave"
					>
						<td>
							<a :href="`/${orgCode}/settings/invoice/${invoice.Id}`" target="_blank" :class="{ 'past-due-text': isPastDueInvoice(invoice) }">{{ formatDate(invoice.Date) }}</a>
						</td>
						<td v-if="useNewBillingPipeline" :class="{ 'past-due-text': isPastDueInvoice(invoice) }">{{ getStatusDisplay(invoice) }}</td>
						<td class="text-right" :class="{ 'past-due-text': isPastDueInvoice(invoice) }">{{ currency.format(invoice.Amount) }}</td>
					</tr>
				</tbody>
			</table>

			<!-- Time-travel investigation aid: full invoice breakdown to the right of the hovered invoice. -->
			<div
				v-if="isTimeTraveling && hoveredLineItems.length"
				class="billing-invoice-tooltip absolute left-full top-0 ml-4 z-10 w-max min-w-[16rem] p-3 bg-shade-3 border border-subtle shadow"
				:style="{ transform: `translateY(${tooltipTop}px)` }"
			>
				<p class="font-bold mb-2 whitespace-nowrap">{{ hoveredInvoiceLabel }}</p>
				<table class="w-full">
					<tbody>
						<tr v-for="(item, index) in hoveredLineItemRows" :key="`li-${index}`" class="border-t border-subtle">
							<th class="text-left py-1 pr-6 font-normal align-top whitespace-nowrap">{{ item.Description }}</th>
							<td class="text-right py-1 align-top whitespace-nowrap">{{ currency.format(item.Amount) }}</td>
						</tr>
					</tbody>
					<tbody class="billing-invoice-tooltip__summary">
						<tr v-for="(item, index) in hoveredSummaryRows" :key="`sum-${index}`" :class="{ 'font-bold': item.Type === 'AmountDue' }">
							<th class="text-right py-1 pr-6 font-normal align-top whitespace-nowrap">{{ item.Description }}</th>
							<td class="text-right py-1 align-top whitespace-nowrap">{{ currency.format(item.Amount) }}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</section>
</template>

<script lang="ts">
	import { defineComponent } from 'vue';
	import type { PropType } from 'vue';
	import type { BillingInvoiceViewModel } from '@cognitoforms/types/server-types/services/view-models/billing-invoice-view-model';
	import { InvoiceStatus } from '@cognitoforms/types/server-types/plans/model/invoice-status';
	import { InvoiceCategory } from '@cognitoforms/types/server-types/plans/model/invoice-category';
	import { isOpenPayableInvoice } from './billing-invoice-utils';
	import session from '@cognitoforms/api/services/session';
	import { FeatureFlags } from '@cognitoforms/types/server-types/feature-flags';
	import CCallout from 'src/components/content/Callout.vue';

	export default defineComponent({
		name: 'CBillingTable',
		components: {
			CCallout
		},
		props: {
			invoices: { type: Array as PropType<BillingInvoiceViewModel[]>, required: true },
			orgCode: { type: String, required: true },
			delinquentAt: { type: [String, Date] as PropType<string | Date | null>, required: false, default: null },
			downgradeAt: { type: [String, Date] as PropType<string | Date | null>, required: false, default: null },
			simulatedUtcNow: { type: [String, Date] as PropType<string | Date | null>, required: false, default: null },
			isAnnual: { type: Boolean as PropType<boolean | null>, required: false, default: null },
			isTimeTraveling: { type: Boolean, default: false }
		},
		emits: ['pay-now'],
		data() {
			// When the new billing pipeline is on, refund rows can carry negative Amounts;
			// 'accounting' renders those as ($X.XX). Otherwise keep the legacy 'standard' style.
			const useNewBillingPipeline = !!session.value?.enabledFeatures?.[FeatureFlags.NewBillingPipeline];
			return {
				useNewBillingPipeline,
				hoveredInvoiceId: null as string | null,
				tooltipTop: 0,
				summaryLineItemTypes: ['Subtotal', 'CreditApplied', 'TotalBeforeDiscount', 'Discount', 'Tax', 'AmountDue'],
				currency: new Intl.NumberFormat('en-US', {
					style: 'currency',
					currency: 'USD',
					currencySign: useNewBillingPipeline ? 'accounting' : 'standard'
				})
			};
		},
		computed: {
			tableTitle() {
				return this.useNewBillingPipeline ? 'Invoices' : 'Billing History';
			},
			hasInvoices() {
				return this.sortedInvoices.length > 0;
			},
			hasPastDueServiceInvoices() {
				return !!this.parseDate(this.delinquentAt) || this.sortedInvoices.some(invoice => this.isPastDueInvoice(invoice) && this.isPayableInvoice(invoice));
			},
			hasPastDuePrepaymentInvoices() {
				return this.sortedInvoices.some(invoice => this.isPrepaymentInvoice(invoice) && this.isPayableInvoice(invoice));
			},
			hasPastDuePrepaymentOnlyInvoices() {
				return this.hasPastDuePrepaymentInvoices && !this.hasPastDueServiceInvoices;
			},
			isRestrictedPeriod() {
				if (!this.hasPastDueServiceInvoices)
					return false;

				const delinquentDate = this.parseDate(this.delinquentAt);
				const effectiveNow = this.parseDate(this.simulatedUtcNow)?.valueOf() ?? Date.now();
				return !!delinquentDate && delinquentDate.valueOf() <= effectiveNow;
			},
			showPastDueAlert() {
				return this.useNewBillingPipeline && (this.hasPastDueServiceInvoices || this.hasPastDuePrepaymentOnlyInvoices);
			},
			pastDueAlertMessage() {
				if (this.hasPastDueServiceInvoices) {
					if (this.isRestrictedPeriod) {
						const downgradeDate = this.parseDate(this.downgradeAt);
						if (downgradeDate)
							return `Your invoice is past due and your organization is now restricted. Please pay the invoice by ${this.formatDate(downgradeDate)} to restore full access to your organization before it is downgraded.`;

						return 'Your invoice is past due and your organization is now restricted. Please pay the invoice to restore full access to your organization before it is downgraded.';
					}

					const delinquentDate = this.parseDate(this.delinquentAt);
					if (delinquentDate)
						return `Your invoice is past due. Pay by ${this.formatDate(delinquentDate)} to avoid service interruptions.`;

					return 'Your invoice is past due. Pay now to avoid service interruptions.';
				}

				return 'You have an invoice that is past due, please pay now to avoid service interruptions.';
			},
			sortedInvoices() {
				return this.invoices.slice().sort((a, b) => new Date(b.Date).valueOf() - new Date(a.Date).valueOf());
			},
			hoveredInvoice(): BillingInvoiceViewModel | null {
				return this.sortedInvoices.find(invoice => invoice.Id === this.hoveredInvoiceId) ?? null;
			},
			hoveredLineItems() {
				return this.hoveredInvoice?.LineItems ?? [];
			},
			hoveredLineItemRows() {
				return this.hoveredLineItems.filter(item => !this.summaryLineItemTypes.includes(item.Type));
			},
			hoveredSummaryRows() {
				return this.hoveredLineItems.filter(item => this.summaryLineItemTypes.includes(item.Type));
			},
			hoveredInvoiceLabel() {
				if (!this.hoveredInvoice)
					return '';

				const label = this.hoveredInvoice.IsHidden ? 'Hidden Revenue Invoice' : 'Invoice';
				return `${label} · ${this.formatDate(this.hoveredInvoice.Date)}`;
			}
		},
		methods: {
			onRowEnter(invoice: BillingInvoiceViewModel, event: MouseEvent) {
				if (!this.isTimeTraveling)
					return;

				this.hoveredInvoiceId = invoice.Id;

				const container = this.$refs.container as HTMLElement | undefined;
				const row = event.currentTarget as HTMLElement | null;
				if (container && row)
					this.tooltipTop = Math.max(0, row.getBoundingClientRect().top - container.getBoundingClientRect().top);
			},
			onRowLeave() {
				if (!this.isTimeTraveling)
					return;

				this.hoveredInvoiceId = null;
			},
			parseDate(value: string | Date | null | undefined): Date | null {
				if (!value)
					return null;

				const parsedDate = new Date(value);
				return Number.isNaN(parsedDate.valueOf()) ? null : parsedDate;
			},
			formatDate(value: string | Date) {
				return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'UTC' });
			},
			isPastDueInvoice(invoice: BillingInvoiceViewModel) {
				return this.isOpenInvoice(invoice) && this.isRevenueInvoice(invoice);
			},
			isOpenInvoice(invoice: BillingInvoiceViewModel) {
				return invoice.Status === InvoiceStatus.Open;
			},
			isPayableInvoice(invoice: BillingInvoiceViewModel) {
				return isOpenPayableInvoice(invoice, this.isAnnual);
			},
			isRevenueInvoice(invoice: BillingInvoiceViewModel) {
				return invoice.Category === InvoiceCategory.Revenue;
			},
			isPrepaymentInvoice(invoice: BillingInvoiceViewModel) {
				return invoice.Category === InvoiceCategory.Prepayment;
			},
			getStatusDisplay(invoice: BillingInvoiceViewModel) {
				if (this.isPastDueInvoice(invoice))
					return 'Past Due';

				return invoice.Status;
			}
		}
	});
</script>

<style lang="scss">
	.org-settings__plan-and-billing-table {

		.past-due-text {
			color: var(--jaywalk) !important;
		}

		.hidden-invoice-row {
			color: var(--text-subtle);
			font-style: italic;

			a {
				color: inherit;
			}
		}

		.billing-invoice-tooltip {
			pointer-events: none;

			&__summary {
				border-top: 2px solid var(--border-primary);
			}
		}
	}

</style>